import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../shared/Layout';
import PageHeader from '../../shared/PageHeader';
import IconGoBack from '../../../components/icons/IconGoBack';
import useGroupWorkspaceData from './useGroupWorkspaceData';
import GroupWorkspaceTopicSection from './GroupWorkspaceTopicSection';
import WorkspaceOutline from '../../students/workspace/WorkspaceOutline';
import wsManager from '../../../services/wsManager';
import '../../../css/courses.css';
import '../../../css/course-preview.css';
import '../../../css/container-layout.css';
import '../../../css/workspace-activity.css';
import '../../../css/group-workspace.css';

// Group-wide mirror of students/workspace/StudentWorkspaceView.jsx - opened
// from GroupCourses.jsx (a group's own "Курси" section). Same 2-column
// shell, but the header lists every group member (with their own online
// dot) instead of one student, and the body merges every member's activity
// into a single scroll via GroupWorkspaceTopicSection rather than showing
// one student's.
export default function GroupCourseWorkspaceView() {
   const { groupId, courseId } = useParams();
   const navigate = useNavigate();
   const location = useLocation();
   const data = useGroupWorkspaceData(groupId, courseId);
   const sectionElsRef = useRef(new Map());
   const inViewRef = useRef(new Set());
   const scrollColRef = useRef(null);
   const [activeTopicId, setActiveTopicId] = useState(null);

   const topics = data.status === 'ready' ? data.topics : [];
   const members = data.status === 'ready' ? data.members : (data.members || []);

   // Same "which topic is currently scrolled into view" scrollspy as
   // StudentWorkspaceView.jsx - was missing here entirely (this whole tree
   // column got dropped when the group view was first built), leaving the
   // group preview with no way to jump to a topic without manually
   // scrolling past everyone's merged activity to find it.
   useEffect(() => {
      if (data.status !== 'ready' || topics.length === 0) return undefined;
      const orderedIds = topics.map(t => t.topic.id);
      const observer = new IntersectionObserver((entries) => {
         entries.forEach(entry => {
            const topicId = Number(entry.target.dataset.topicId);
            if (entry.isIntersecting) inViewRef.current.add(topicId);
            else inViewRef.current.delete(topicId);
         });
         const active = orderedIds.filter(id => inViewRef.current.has(id)).pop();
         if (active != null) setActiveTopicId(active);
      }, { rootMargin: '0px 0px -65% 0px', threshold: 0 });
      sectionElsRef.current.forEach(el => { if (el) observer.observe(el); });
      return () => observer.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.status, topics.length]);

   const jumpToCourse = () => { if (scrollColRef.current) scrollColRef.current.scrollTo({ top: 0, behavior: 'smooth' }); };
   const jumpToTopic = (topicId) => {
      const el = sectionElsRef.current.get(topicId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
   };

   // One online/offline boolean PER MEMBER, aggregated client-side from N
   // separate /topic/workspace/{id}/presence subscriptions - the backend
   // presence mechanism itself is untouched (still one boolean per
   // workspace), this view just listens to all of them at once instead of
   // one.
   const [onlineByStudentId, setOnlineByStudentId] = useState({});
   useEffect(() => {
      if (data.status !== 'ready') return undefined;
      const activeMembers = members.filter(m => m.workspaceId);
      const handlers = activeMembers.map(m => {
         const topic = '/topic/workspace/' + m.workspaceId + '/presence';
         const handler = (event) => {
            if (event.type === 'presence_online') setOnlineByStudentId(prev => ({ ...prev, [m.studentId]: true }));
            else if (event.type === 'presence_offline') setOnlineByStudentId(prev => ({ ...prev, [m.studentId]: false }));
         };
         wsManager.subscribe(topic, handler);
         return { topic, handler };
      });
      return () => { handlers.forEach(h => wsManager.unsubscribe(h.topic, h.handler)); };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.status, members.map(m => m.workspaceId).join(',')]);

   const registerSectionEl = (topicId, el) => {
      if (el) sectionElsRef.current.set(topicId, el);
      else sectionElsRef.current.delete(topicId);
   };

   // Same `?topic=` deep-link jump as StudentWorkspaceView.jsx - was missing
   // here (this view never read useLocation at all), so a topic pill from
   // GroupLessonsList.jsx could only land on the course, not scroll to the
   // exact linked topic.
   const jumpedToQueryTopicRef = useRef(false);
   useEffect(() => {
      if (jumpedToQueryTopicRef.current) return;
      if (data.status !== 'ready' || topics.length === 0) return;
      const topicParam = new URLSearchParams(location.search).get('topic');
      if (!topicParam) { jumpedToQueryTopicRef.current = true; return; }
      const targetId = Number(topicParam);
      const tryJump = () => {
         const el = sectionElsRef.current.get(targetId);
         if (el) {
            jumpedToQueryTopicRef.current = true;
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
         }
      };
      tryJump();
      const t = setTimeout(tryJump, 400);
      return () => clearTimeout(t);
   }, [data.status, topics.length, location.search]);

   const backToGroup = () => navigate('/groups/' + groupId);

   return (
      <Layout>
         <PageHeader name={
            <div className='course-workspace-header-inner'>
               <div className='course-workspace-back-btn' onClick={backToGroup} title='До групи'>
                  <IconGoBack />
               </div>
               <span className='course-workspace-title'>
                  {data.status === 'ready' && data.course ? data.course.name : 'Груповий курс'}
               </span>
            </div>
         }>
            {members.length > 0 ? (
               <div className='group-workspace-members-strip'>
                  {members.map(m => (
                     <span key={m.studentId} className={'group-workspace-member-chip' + (onlineByStudentId[m.studentId] ? ' online' : '')}
                        onClick={() => navigate('/students/' + m.studentId + '/workspace/' + courseId)}
                        title={onlineByStudentId[m.studentId] ? 'Студент онлайн' : 'Студент офлайн'}>
                        <i className='group-workspace-member-dot' />
                        {m.name}
                     </span>
                  ))}
               </div>
            ) : ''}
         </PageHeader>
         <main className='main-block'>
            <div className='main-content txt-c course-workspace-page'>
               {data.status === 'loading' ? (
                  <div className='event-edit-loading'>Завантаження...</div>
               ) : data.status === 'not-started' ? (
                  <div className='workspace-not-started'>
                     <p>Жоден учасник групи ще не відкривав цей курс на платформі.</p>
                  </div>
               ) : data.status === 'error' ? (
                  <div className='event-edit-loading'>Не вдалося завантажити дані.</div>
               ) : (
                  <div className='course-workspace-body'>
                     <div className='course-workspace-tree-col'>
                        <WorkspaceOutline course={data.course} topics={topics} activeTopicId={activeTopicId}
                           onJumpToCourse={jumpToCourse} onJumpToTopic={jumpToTopic} />
                     </div>
                     <div className='course-workspace-detail-col' ref={scrollColRef}>
                        <div className='course-preview'>
                           {topics.length === 0 ? (
                              <div className='course-preview-empty'>Тут ще немає жодної теми з контентом.</div>
                           ) : topics.map(topicEntry => (
                              <GroupWorkspaceTopicSection key={topicEntry.topic.id} topicEntry={topicEntry}
                                 notesByTarget={data.notesByTarget} members={members.filter(m => m.workspaceId)}
                                 activityByStudentId={data.activityByStudentId} workspaceId={data.representativeWorkspaceId}
                                 groupId={groupId} courseId={courseId} onNotesChanged={data.refreshNotes}
                                 sectionRef={(el) => registerSectionEl(topicEntry.topic.id, el)} />
                           ))}
                        </div>
                     </div>
                  </div>
               )}
            </div>
         </main>
      </Layout>
   );
}
